// Map Drizzle column metadata to ZenStack field info declarations
// FEATURE: zen column type mapping for schema generation
import type { EnumInfo, FieldInfo } from "./types";
import { esc } from "./utils";

// col is typed as `any` because we access internal Drizzle column properties
// (name, columnType, notNull, hasDefault, primary, baseColumn, enum, defaultFn, etc.)
// that are not exposed on the public AnyPgColumn type.
export function mapColumn(jsKey: string, col: any, enumMap: Map<string, EnumInfo>): FieldInfo {
  const sqlName: string = col.name;
  const colType: string = col.columnType;
  const notNull: boolean = col.notNull;
  const hasDefault: boolean = col.hasDefault;
  const isPrimary: boolean = col.primary;
  const isUnique: boolean = col.isUnique;
  const generated = col.generated;
  const isArray = colType === "PgArray";

  let type = "String";
  const dbAttrs: string[] = [];
  let enumPgName: string | null = null;
  let isJsonColumn = false;
  let actualColType = colType;
  let actualCol = col;

  if (isArray && col.baseColumn) {
    actualCol = col.baseColumn;
    actualColType = actualCol.columnType;
  }

  switch (actualColType) {
    case "PgUUID":
      type = "String";
      dbAttrs.push("@db.Uuid");
      break;
    case "PgText":
      type = "String";
      break;
    case "PgVarchar": {
      type = "String";
      const len = actualCol.length;
      if (len) dbAttrs.push(`@db.VarChar(${len})`);
      else dbAttrs.push("@db.VarChar");
      break;
    }
    case "PgBoolean":
      type = "Boolean";
      break;
    case "PgInteger":
      type = "Int";
      break;
    case "PgBigInt53":
      type = "BigInt";
      break;
    case "PgSerial":
      type = "Int";
      break;
    case "PgReal":
      type = "Float";
      dbAttrs.push("@db.Real");
      break;
    case "PgDoublePrecision":
      type = "Float";
      dbAttrs.push("@db.DoublePrecision");
      break;
    case "PgTimestamp": {
      type = "DateTime";
      const wtz = actualCol.withTimezone ?? actualCol.config?.withTimezone;
      if (wtz) dbAttrs.push("@db.Timestamptz(6)");
      else dbAttrs.push("@db.Timestamp(6)");
      break;
    }
    case "PgDate":
    case "PgDateString":
      type = "DateTime";
      dbAttrs.push("@db.Date");
      break;
    case "PgJsonb":
      type = "Json";
      isJsonColumn = true;
      dbAttrs.push("@db.JsonB");
      break;
    case "PgJson":
      type = "Json";
      isJsonColumn = true;
      dbAttrs.push("@db.Json");
      break;
    case "PgEnumColumn": {
      if (isArray) {
        type = "String";
      } else {
        const eName = actualCol.enum?.enumName;
        if (eName && enumMap.has(eName)) {
          type = enumMap.get(eName)!.zenstackName;
          enumPgName = eName;
        } else {
          type = "String";
        }
      }
      break;
    }
    case "PgCustomColumn": {
      const sqlType = actualCol.sqlName || actualCol.config?.dataType;
      if (sqlType === "json") {
        type = "Json";
        isJsonColumn = true;
        dbAttrs.push("@db.Json");
      } else if (sqlType === "jsonb") {
        type = "Json";
        isJsonColumn = true;
        dbAttrs.push("@db.JsonB");
      } else if (sqlType === "numeric") {
        type = "Decimal";
      } else type = "String";
      break;
    }
    case "PgNumeric":
      type = "Decimal";
      break;
    default:
      console.warn(
        `Unknown Drizzle column type "${actualColType}" for column "${sqlName}" — falling back to String`,
      );
      type = "String";
  }

  let defaultExpr: string | null = null;
  if (hasDefault) {
    if (isPrimary && actualColType === "PgUUID") {
      defaultExpr = 'ExpressionUtils.call("uuid")';
    } else if (actualColType === "PgBigInt53" && col.generatedIdentity) {
      defaultExpr = 'ExpressionUtils.call("autoincrement")';
    } else if (actualColType === "PgSerial") {
      defaultExpr = 'ExpressionUtils.call("autoincrement")';
    } else if (col.defaultFn) {
      if (type === "DateTime") {
        defaultExpr = 'ExpressionUtils.call("now")';
      } else if (type === "String" && isPrimary) {
        defaultExpr = 'ExpressionUtils.call("uuid")';
      } else {
        defaultExpr = null;
      }
    } else if (col.default !== undefined) {
      const d = col.default;
      if (typeof d === "boolean" || typeof d === "number") {
        defaultExpr = `ExpressionUtils.literal(${d})`;
      } else if (typeof d === "string") {
        defaultExpr = `ExpressionUtils.literal(${esc(d)})`;
      } else if (d && typeof d === "object" && "queryChunks" in d) {
        try {
          const chunks = d.queryChunks || [];
          const sqlStr = chunks
            .map((c: any) => (typeof c === "string" ? c : c.value?.[0] || ""))
            .join("")
            .trim();
          if (sqlStr) {
            defaultExpr = `ExpressionUtils.call("dbgenerated", [ExpressionUtils.literal(${esc(sqlStr)})])`;
          }
        } catch (e) {
          console.warn(`Failed to parse SQL default for column "${sqlName}":`, e);
        }
      } else if (Array.isArray(d) && d.length === 0) {
        defaultExpr = "[] as FieldDefault";
      } else if (typeof d === "object" && d !== null && Object.keys(d).length === 0) {
        defaultExpr = `ExpressionUtils.literal("{}")`;
      }
    }
  }

  const isDateTimeScalar = type === "DateTime" && !isArray;
  const isCreatedAt = isDateTimeScalar && (jsKey === "createdAt" || sqlName === "created_at");
  const isUpdatedAt = isDateTimeScalar && (jsKey === "updatedAt" || sqlName === "updated_at");

  if (isCreatedAt && !defaultExpr) {
    defaultExpr = 'ExpressionUtils.call("now")';
  }

  return {
    name: jsKey,
    sqlName,
    type,
    isJsonColumn,
    optional: !notNull && !hasDefault,
    array: isArray,
    id: isPrimary,
    unique: isUnique,
    hasDefault: hasDefault || isCreatedAt || isUpdatedAt,
    dbAttrs,
    defaultExpr,
    enumPgName,
    isGenerated: !!generated,
    createdAt: isCreatedAt,
    updatedAt: isUpdatedAt,
  };
}
